import type { QueueFilters, Stage, Status, QueueTiles as Tiles } from '@hrms/shared'
import type { IconType } from 'react-icons'
import { LuActivity, LuAlarmClock, LuCircleCheck, LuHourglass, LuInbox } from 'react-icons/lu'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import type { FilterUpdate } from './useQueueParams.ts'

interface TileDef {
  id: keyof Tiles
  label: string
  icon: IconType
  iconClass: string
  tip: string
  stage?: Stage
}

const TILES: TileDef[] = [
  {
    id: 'new',
    label: 'Open',
    icon: LuInbox,
    iconClass: 'text-blue-fg',
    stage: 'new',
    tip: 'Tickets in the New stage (Open, Reopened): not picked up yet. Click to show only these.',
  },
  {
    id: 'in_progress',
    label: 'In progress',
    icon: LuActivity,
    iconClass: 'text-violet-fg',
    stage: 'in_progress',
    tip: 'Tickets in the In progress stage: HR owns the next step. Click to show only these.',
  },
  {
    id: 'waiting',
    label: 'Waiting on employee',
    icon: LuHourglass,
    iconClass: 'text-amber-fg',
    stage: 'waiting',
    tip: 'Waiting for the employee to reply. The ETA is paused. Click to show only these.',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    icon: LuAlarmClock,
    iconClass: 'text-rose-fg',
    tip: 'Past their ETA, not counting Done or Waiting tickets. Click to show only these.',
  },
  {
    id: 'done',
    label: 'Resolved',
    icon: LuCircleCheck,
    iconClass: 'text-green-fg',
    stage: 'done',
    tip: 'Tickets in the Done stage (Resolved, Closed). Click to show only these.',
  },
]

/**
 * Five summary tiles (PRD "Summary tiles"). Counts use every filter except status, stage and
 * overdue. Clicking a stage tile sets the Stage filter to just that stage (keeping only the
 * chosen statuses inside it), and clicking it again clears Stage; Overdue toggles Overdue.
 */
export function QueueTiles({
  tiles,
  filters,
  setFilters,
  statuses,
}: {
  tiles: Tiles | undefined
  filters: QueueFilters
  setFilters: (patch: FilterUpdate) => void
  statuses: readonly Status[]
}) {
  return (
    <div
      data-tour="queue-tiles"
      className="mb-4 grid grid-cols-5 gap-px overflow-hidden rounded-panel border border-border bg-border max-[980px]:grid-cols-3 max-[720px]:grid-cols-2"
    >
      {TILES.map((t) => {
        const stage = t.stage
        const pressed = stage
          ? filters.stage?.length === 1 && filters.stage[0] === stage
          : !!filters.overdue
        const n = tiles?.[t.id]
        const Icon = t.icon
        return (
          <button
            key={t.id}
            type="button"
            aria-pressed={pressed}
            onClick={() => {
              if (!stage) return setFilters({ overdue: pressed ? '' : '1' })
              if (pressed) return setFilters({ stage: '' })
              const inStage = new Set(statuses.filter((s) => s.stage === stage).map((s) => s.id))
              setFilters({
                stage: [stage],
                status: filters.status?.filter((id) => inStage.has(id)),
              })
            }}
            className={cn(
              'flex flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors max-[720px]:last:col-span-2',
              pressed ? 'bg-accent-soft' : 'bg-surface hover:bg-surface-2',
            )}
            {...hint(t.tip)}
          >
            <span
              className={cn(
                'tabular text-[22px] leading-tight font-semibold',
                t.id === 'overdue' && (n ?? 0) > 0 && 'text-danger',
              )}
            >
              {n ?? '–'}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[13px] text-muted">
              <Icon aria-hidden className={cn('size-3.5', t.iconClass)} />
              {t.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
